// src/pages/Produtos.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  getFirestore,
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  orderBy,
  updateDoc,
  addDoc,
  Timestamp,
  getDoc,
} from 'firebase/firestore';
import { getAuth, onIdTokenChanged } from 'firebase/auth';
import {
  FaPlus,
  FaTrashAlt,
  FaEdit,
  FaExchangeAlt,
  FaHistory,
  FaFileInvoice,
  FaExclamationCircle,
  FaClipboardList,
  FaBoxes,
  FaMoneyBillWave,
  FaShoppingCart,
  FaFilter,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

import { useEmpresaId } from '../context/EmpresaContext';
import ModalNovoProduto from '../components/ModalNovoProduto';
import ModalConsultaNfe from '../components/ModalConsultaNfe';
import MovimentacaoEstoqueModal from '../components/MovimentacaoEstoqueModal';
import HistoricoMovimentacoesModal from '../components/HistoricoMovimentacoesModal';
import ModalNotasManifestadas from '../components/ModalManifestoNfe';

const formatMoney = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDateTimeBR = (v) => {
  if (!v) return '-';
  let d = null;

  // Firestore Timestamp
  if (typeof v === 'object' && typeof v.toDate === 'function') d = v.toDate();
  else if (v instanceof Date) d = v;
  else if (typeof v === 'number') d = new Date(v);
  else if (typeof v === 'string') {
    const parsed = new Date(v);
    d = Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (!d || Number.isNaN(d.getTime())) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
};

const toMillis = (v) => {
  if (!v) return 0;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (typeof v?.toDate === 'function') return v.toDate().getTime();
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const db = getFirestore();

const CSOSN_VALIDOS = ['101', '102', '103', '201', '202', '203', '300', '400', '500', '900'];
const CST_VALIDOS = ['00', '10', '20', '30', '40', '41', '50', '51', '60', '70', '90'];

function fiscalInvalido(prod, regime) {
  if (regime === '1' || regime === '4') {
    return !CSOSN_VALIDOS.includes(String(prod.csosn || '').padStart(3, '0'));
  }
  return !CST_VALIDOS.includes(String(prod.cst || '').padStart(2, '0'));
}

const colProdutos = (empresaId) => collection(db, 'empresas', empresaId, 'produtos');
const refProduto = (empresaId, id) => doc(db, 'empresas', empresaId, 'produtos', id);
const colMovs = (empresaId, id) =>
  collection(db, 'empresas', empresaId, 'produtos', id, 'movimentacoes');

const toneStyles = (tone) => {
  switch (tone) {
    case 'primary':
      return 'text-blue-800 bg-blue-100 hover:bg-blue-200 dark:text-blue-300 dark:bg-blue-900/50 dark:hover:bg-blue-900';
    case 'warning':
      return 'text-yellow-800 bg-yellow-100 hover:bg-yellow-200 dark:text-yellow-300 dark:bg-yellow-900/50';
    case 'neutral':
      return 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700';
    case 'danger':
      return 'text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-300 dark:bg-red-900/50 dark:hover:bg-red-900';
    default:
      return 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700';
  }
};

const ActionIconBtn = ({ tone = 'neutral', title, children, ...props }) => (
  <button
    title={title}
    aria-label={title}
    className={`inline-flex items-center justify-center w-8 h-8 rounded-md text-xs font-semibold ${toneStyles(
      tone
    )}`}
    {...props}
  >
    {children}
  </button>
);

// páginas com reticências
function buildPages(curr, total) {
  if (total <= 6) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, curr, curr - 1, curr + 1, curr - 2, curr + 2]);
  const arr = Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < arr.length; i++) {
    out.push(arr[i]);
    if (i < arr.length - 1 && arr[i + 1] > arr[i] + 1) out.push('…');
  }
  return out;
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white/90 dark:bg-gray-900/70 backdrop-blur p-4 shadow-sm hover:shadow-md transition">
    <div className="flex items-center gap-3">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#004AAD]/10 to-transparent dark:from-blue-500/20">
        <Icon className="text-[#004AAD] dark:text-blue-400 text-lg" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {label}
        </div>
        <div className="text-xl font-bold text-gray-900 dark:text-white truncate">{value}</div>
      </div>
    </div>
  </div>
);

const Produtos = () => {
  const empresaId = useEmpresaId();

  const [produtos, setProdutos] = useState([]);
  const [totalEstoque, setTotalEstoque] = useState(0);
  const [totalValorBruto, setTotalValorBruto] = useState(0);
  const [totalValorVenda, setTotalValorVenda] = useState(0);

  const [busca, setBusca] = useState('');
  const [formVisivel, setFormVisivel] = useState(false);
  const [editandoProduto, setEditandoProduto] = useState(null);
  const [movimentarProduto, setMovimentarProduto] = useState(null);
  const [historicoProduto, setHistoricoProduto] = useState(null);
  const [modalNfe, setModalNfe] = useState(false);
  const [modalManifestos, setModalManifestos] = useState(false);
  const [token, setToken] = useState('');

  const [regimeTributario, setRegimeTributario] = useState('1');

  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);

  // toggle para filtrar apenas inválidos
  const [apenasInvalidos, setApenasInvalidos] = useState(false);

  // ordenação da tabela (clicável no cabeçalho)
  const [sort, setSort] = useState({ key: 'criadoEm', dir: 'desc' }); // padrão: mais novos primeiro

  const toggleSort = (key) => {
    setSort((s) => {
      if (s.key === key) return { key, dir: s.dir === 'asc' ? 'desc' : 'asc' };
      return { key, dir: key === 'nome' ? 'asc' : 'desc' };
    });
  };

  const SortIcon = ({ active, dir }) => {
    if (!active) return <FaSort className="opacity-40" />;
    return dir === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  useEffect(() => {
    const auth = getAuth();
    const unsub = onIdTokenChanged(auth, async (user) => {
      if (user) {
        try {
          setToken(await user.getIdToken());
        } catch {
          setToken('');
        }
      } else setToken('');
    });
    return () => unsub && unsub();
  }, []);

  useEffect(() => {
    if (!empresaId) {
      setProdutos([]);
      setTotalEstoque(0);
      setTotalValorBruto(0);
      setTotalValorVenda(0);
      return;
    }
    buscarRegimeTributario(empresaId);
    buscarProdutos(empresaId);
  }, [empresaId]);

  useEffect(() => {
    setPage(1);
  }, [busca, itemsPerPage, produtos.length, apenasInvalidos, sort]);

  const buscarRegimeTributario = async (id) => {
    try {
      const snap = await getDoc(doc(db, 'empresas', id));
      if (snap.exists()) setRegimeTributario(snap.data().regimeTributario || '1');
    } catch {
      setRegimeTributario('1');
    }
  };

  const buscarProdutos = async (id) => {
    try {
      // mantém orderBy('nome') no Firestore para busca estável;
      // a ordenação final é feita no client (useMemo)
      const q = query(colProdutos(id), orderBy('nome'));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProdutos(list);

      const somaEstoque = list.reduce((acc, p) => acc + (Number(p.estoque) || 0), 0);
      const somaBruto = list.reduce(
        (acc, p) => acc + (Number(p.precoBruto) || 0) * (Number(p.estoque) || 0),
        0
      );
      const somaVenda = list.reduce(
        (acc, p) => acc + (Number(p.precoVenda) || 0) * (Number(p.estoque) || 0),
        0
      );
      setTotalEstoque(somaEstoque);
      setTotalValorBruto(somaBruto);
      setTotalValorVenda(somaVenda);
    } catch (e) {
      console.error(e);
      setProdutos([]);
      setTotalEstoque(0);
      setTotalValorBruto(0);
      setTotalValorVenda(0);
    }
  };

  const handleExcluir = async (id) => {
    const r = await Swal.fire({
      title: 'Deseja excluir este produto?',
      text: 'Esta ação não poderá ser desfeita.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
    });
    if (!r.isConfirmed) return;
    await deleteDoc(refProduto(empresaId, id));
    await buscarProdutos(empresaId);
    Swal.fire('Excluído!', 'O produto foi removido.', 'success');
  };

  // número de itens inválidos (para o banner)
  const totalInvalidos = useMemo(
    () => produtos.reduce((acc, p) => acc + (fiscalInvalido(p, regimeTributario) ? 1 : 0), 0),
    [produtos, regimeTributario]
  );

  const produtosFiltrados = useMemo(
    () =>
      produtos
        .filter((p) => !apenasInvalidos || fiscalInvalido(p, regimeTributario))
        .filter(
          (p) =>
            (p.nome?.toLowerCase() || '').includes(busca.toLowerCase()) ||
            (p.sku?.toLowerCase() || '').includes(busca.toLowerCase())
        )
        .sort((a, b) => {
          if (sort.key === 'nome') {
            const cmp = (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' });
            return sort.dir === 'asc' ? cmp : -cmp;
          }

          // sort.key === 'criadoEm'
          const diff = toMillis(a.criadoEm) - toMillis(b.criadoEm);
          if (diff !== 0) return sort.dir === 'asc' ? diff : -diff;

          // fallback: nome
          const cmp = (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' });
          return cmp;
        }),
    [produtos, busca, apenasInvalidos, regimeTributario, sort]
  );

  const totalItems = produtosFiltrados.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const clampedPage = Math.min(page, totalPages);
  const startIdx = (clampedPage - 1) * itemsPerPage;
  const endIdx = Math.min(startIdx + itemsPerPage, totalItems);
  const pageItems = produtosFiltrados.slice(startIdx, endIdx);

  const btn = 'inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow transition';
  const btnPrimary = `${btn} bg-blue-600 hover:bg-blue-700 text-white`;
  const btnSuccess = `${btn} bg-green-600 hover:bg-green-700 text-white`;
  const btnIndigo = `${btn} bg-indigo-600 hover:bg-indigo-700 text-white`;

  return (
    <div className="flex h-full relative">
      <main className="flex-grow w-full max-w-none mx-auto p-4 sm:p-6">
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Produtos cadastrados</h3>

          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="text"
              placeholder="Buscar por nome ou SKU"
              className="border px-3 py-2 rounded-md w-72 bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />

            <button
              onClick={() => {
                setFormVisivel(true);
                setEditandoProduto(null);
              }}
              className={btnPrimary}
            >
              <FaPlus /> Novo Produto
            </button>

            <button onClick={() => setModalNfe(true)} className={btnSuccess}>
              <FaFileInvoice /> Consultar NFe
            </button>

            <button
              onClick={() => setModalManifestos(true)}
              className={btnIndigo}
              title="Ver notas já manifestadas (ciência/confirmadas)"
            >
              <FaClipboardList /> Notas manifestadas
            </button>
          </div>
        </div>

        {/* ===== Banner de aviso fiscal no topo ===== */}
        {totalInvalidos > 0 && (
          <div
            className="mt-4 mb-2 rounded-xl border border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200 p-4"
            role="alert"
            aria-live="polite"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <FaExclamationCircle className="text-rose-600 dark:text-rose-300" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">
                  Atenção: {totalInvalidos} {totalInvalidos === 1 ? 'produto' : 'produtos'} com campos
                  fiscais inválidos para o regime tributário.
                </p>
                <p className="text-sm opacity-90">
                  Edite os itens sinalizados para evitar erros em notas fiscais e cálculos de impostos.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setApenasInvalidos((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold
                             bg-white text-rose-700 border border-rose-200 hover:bg-rose-100
                             dark:bg-rose-950/20 dark:text-rose-200 dark:border-rose-900/60 dark:hover:bg-rose-900/40"
                  title={apenasInvalidos ? 'Mostrar todos' : 'Mostrar apenas inválidos'}
                >
                  <FaFilter />
                  {apenasInvalidos ? 'Mostrar todos' : 'Mostrar apenas inválidos'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard icon={FaBoxes} label="Total de itens em estoque" value={totalEstoque} />
          <StatCard
            icon={FaMoneyBillWave}
            label="Valor total bruto"
            value={`R$ ${formatMoney(totalValorBruto)}`}
          />
          <StatCard
            icon={FaShoppingCart}
            label="Valor total de venda"
            value={`R$ ${formatMoney(totalValorVenda)}`}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between mb-3 gap-3 text-sm text-gray-600 dark:text-gray-300">
          <div>
            {totalItems > 0 ? (
              <>
                Mostrando <b>{startIdx + 1}</b>–<b>{endIdx}</b> de <b>{totalItems}</b> itens
              </>
            ) : (
              'Nenhum item'
            )}
          </div>

          <div className="flex items-center gap-2">
            <span>Itens por página:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="border rounded-md px-2 py-1 bg-white dark:bg-gray-800 dark:border-gray-700"
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>

            <div className="ml-3 flex items-center gap-2">
              <button
                className="px-3 py-1 rounded-md border dark:border-gray-700 disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={clampedPage <= 1}
              >
                Anterior
              </button>

              {buildPages(clampedPage, totalPages).map((p, i) =>
                p === '…' ? (
                  <span key={`e-${i}`} className="px-2 text-gray-400">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1 rounded-md border dark:border-gray-700 ${
                      p === clampedPage
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                className="px-3 py-1 rounded-md border dark:border-gray-700 disabled:opacity-50"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={clampedPage >= totalPages}
              >
                Próxima
              </button>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="rounded-lg shadow -mx-4 sm:mx-0">
          {/* sem scroll horizontal (visão única) */}
          <div className="overflow-x-hidden">
            <table className="w-full table-auto text-xs text-left bg-white dark:bg-gray-900 border dark:border-gray-800">
              <thead className="text-[11px] text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <button
                      type="button"
                      onClick={() => toggleSort('nome')}
                      className="inline-flex items-center gap-2 hover:underline"
                      title="Ordenar por Nome"
                    >
                      Nome <SortIcon active={sort.key === 'nome'} dir={sort.dir} />
                    </button>
                  </th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">SKU</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">NCM</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Estoque</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Preço Bruto</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">% Lucro</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Venda</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Total Bruto</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Total Venda</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => toggleSort('criadoEm')}
                      className="inline-flex items-center gap-2 hover:underline"
                      title="Ordenar por Data de criação"
                    >
                      Criado em <SortIcon active={sort.key === 'criadoEm'} dir={sort.dir} />
                    </button>
                  </th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Ações</th>
                </tr>
              </thead>

              <tbody>
                {pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-8 text-gray-500 dark:text-gray-300">
                      Nenhum produto encontrado.
                    </td>
                  </tr>
                ) : (
                  pageItems.map((p) => {
                    const invalido = fiscalInvalido(p, regimeTributario);
                    const nomeTip = p.nome || '';
                    return (
                      <tr
                        key={p.id}
                        className="border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                      >
                        {/* Nome */}
                        <td className="relative group px-3 py-2 font-bold text-gray-900 dark:text-white align-middle">
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="block min-w-0 truncate pr-2">{nomeTip || '-'}</span>
                            {invalido && (
                              <FaExclamationCircle
                                size={14}
                                className="text-rose-600 ml-1 shrink-0"
                                title="Campos fiscais inválidos para o regime tributário!"
                              />
                            )}
                          </div>

                          {/* Tooltip */}
                          <div
                            className="
                              pointer-events-none
                              absolute -top-8 left-3 z-50
                              opacity-0 group-hover:opacity-100 transition-opacity
                              bg-gray-900 text-white text-xs px-2 py-1 rounded shadow
                              whitespace-nowrap
                            "
                          >
                            {nomeTip}
                          </div>
                        </td>

                        <td title={nomeTip} className="px-3 py-2 align-middle text-center truncate">
                          {p.sku || '-'}
                        </td>
                        <td title={nomeTip} className="px-3 py-2 align-middle text-center truncate">
                          {p.ncm || '-'}
                        </td>
                        <td
                          title={nomeTip}
                          className="px-3 py-2 align-middle text-center whitespace-nowrap"
                        >
                          {p.estoque} {p.unidade}
                        </td>
                        <td
                          title={nomeTip}
                          className="px-3 py-2 align-middle text-center whitespace-nowrap"
                        >
                          R$ {formatMoney(p.precoBruto)}
                        </td>
                        <td
                          title={nomeTip}
                          className="px-3 py-2 align-middle text-center whitespace-nowrap"
                        >
                          {formatMoney(Number(p.porcentagemLucro) || 0)}%
                        </td>
                        <td
                          title={nomeTip}
                          className="px-3 py-2 align-middle text-center text-green-700 dark:text-green-400 font-bold whitespace-nowrap"
                        >
                          R$ {formatMoney(Number(p.precoVenda) || 0)}
                        </td>
                        <td
                          title={nomeTip}
                          className="px-3 py-2 align-middle text-center text-gray-700 dark:text-gray-200 font-semibold whitespace-nowrap"
                        >
                          R$ {formatMoney((Number(p.precoBruto) || 0) * (Number(p.estoque) || 0))}
                        </td>
                        <td
                          title={nomeTip}
                          className="px-3 py-2 align-middle text-center text-green-700 dark:text-green-400 font-bold whitespace-nowrap"
                        >
                          R$ {formatMoney((Number(p.precoVenda) || 0) * (Number(p.estoque) || 0))}
                        </td>

                        {/* criadoEm */}
                        <td
                          title={nomeTip}
                          className="px-3 py-2 align-middle text-center whitespace-nowrap text-gray-700 dark:text-gray-200"
                        >
                          {formatDateTimeBR(p.criadoEm)}
                        </td>

                        {/* ações (quebra linha se precisar) */}
                        <td className="px-3 py-2 align-middle">
                          <div className="flex flex-wrap gap-1 justify-center">
                            <ActionIconBtn
                              tone="warning"
                              title="Movimentar Estoque"
                              onClick={() => setMovimentarProduto(p)}
                            >
                              <FaExchangeAlt />
                            </ActionIconBtn>

                            <ActionIconBtn
                              tone="primary"
                              title="Histórico de Movimentações"
                              onClick={() => setHistoricoProduto(p)}
                            >
                              <FaHistory />
                            </ActionIconBtn>

                            <ActionIconBtn
                              tone="neutral"
                              title="Editar Produto"
                              onClick={() => {
                                setFormVisivel(true);
                                setEditandoProduto(p);
                              }}
                            >
                              <FaEdit />
                            </ActionIconBtn>

                            <ActionIconBtn
                              tone="danger"
                              title="Excluir Produto"
                              onClick={() => handleExcluir(p.id)}
                            >
                              <FaTrashAlt />
                            </ActionIconBtn>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <ModalNovoProduto
        open={formVisivel}
        onClose={() => {
          setFormVisivel(false);
          setEditandoProduto(null);
        }}
        onImportar={() => buscarProdutos(empresaId)}
        empresaId={empresaId}
        produtoEdicao={editandoProduto}
      />

      {movimentarProduto && (
        <MovimentacaoEstoqueModal
          produto={movimentarProduto}
          onClose={() => setMovimentarProduto(null)}
          onMovimentar={async ({ tipo, quantidade, observacao }) => {
            const p = movimentarProduto;
            const ref = refProduto(empresaId, p.id);
            let novoEstoque = Number(p.estoque) || 0;
            if (tipo === 'entrada') novoEstoque += Number(quantidade);
            else novoEstoque -= Number(quantidade);

            try {
              await updateDoc(ref, { estoque: novoEstoque });
              await addDoc(colMovs(empresaId, p.id), {
                tipo,
                quantidade: Number(quantidade),
                observacao,
                data: Timestamp.now(),
              });

              setMovimentarProduto(null);
              await buscarProdutos(empresaId);
              Swal.fire({
                icon: 'success',
                title: 'Movimentação registrada!',
                timer: 1800,
                showConfirmButton: false,
              });
            } catch (err) {
              Swal.fire({
                icon: 'error',
                title: 'Erro ao salvar movimentação',
                text: err?.message || 'Tente novamente.',
              });
            }
          }}
        />
      )}

      {historicoProduto && (
        <HistoricoMovimentacoesModal
          open={!!historicoProduto}
          produto={historicoProduto}
          onClose={() => setHistoricoProduto(null)}
        />
      )}

      <ModalConsultaNfe
        open={modalNfe}
        onClose={() => setModalNfe(false)}
        onImportarProdutos={() => buscarProdutos(empresaId)}
        empresaId={empresaId}
      />

      <ModalNotasManifestadas
        open={modalManifestos}
        onClose={() => setModalManifestos(false)}
        empresaId={empresaId}
        token={token}
      />
    </div>
  );
};

export default Produtos;
